
import React, { useState } from 'react';
import { UploadCloud, CheckCircle, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { ZONES } from '../data/mockData';
import { TransactionType, PropertySubtype, Zone } from '../types';

const Publish: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [photosCount, setPhotosCount] = useState(0);
  
  const [formData, setFormData] = useState({
    transactionType: 'Location' as TransactionType,
    propertyType: 'Appartement' as PropertySubtype,
    quartier: ZONES[0] as Zone,
    address: '',
    price: '',
    bedrooms: '1',
    livingRooms: '1',
    bathrooms: '1',
    surface: '',
    furnished: 'Non',
    description: '',
    advertiserType: 'Propriétaire',
    name: '',
    phone: '',
    email: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTransactionTypeChange = (type: TransactionType) => {
      let defaultSubtype: PropertySubtype = 'Appartement';
      if (type === 'Vente') defaultSubtype = 'Maison';
      if (type === 'Meublé') defaultSubtype = 'Appartement meublé';
      
      setFormData(prev => ({ 
          ...prev, 
          transactionType: type,
          propertyType: defaultSubtype
      }));
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (photosCount < 4) {
        alert("Veuillez ajouter au moins 4 photos pour valider votre annonce.");
        return;
    }
    // Simulate API call
    setTimeout(() => {
      setSubmitted(true);
      window.scrollTo(0, 0);
    }, 1000);
  };

  // Dynamic Subtypes Options
  const getSubtypes = () => {
      if (formData.transactionType === 'Location') return ['Appartement', 'Entrée personnelle', 'Boutique', 'Bureau', 'Parcelle'];
      if (formData.transactionType === 'Vente') return ['Maison', 'Immeuble', 'Parcelle Nu', 'Parcelle avec Construction', 'Fonds de commerce'];
      return ['Appartement meublé'];
  }

  // Determine if bedroom/bathroom fields should be shown
  const showRoomFields = !['Parcelle', 'Parcelle Nu', 'Parcelle avec Construction', 'Boutique', 'Fonds de commerce'].includes(formData.propertyType);

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-10 rounded-3xl shadow-xl max-w-lg w-full text-center">
          <div className="w-24 h-24 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle size={48} />
          </div>
          <h2 className="text-3xl font-bold text-navy mb-4">Annonce soumise !</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Votre bien est en cours de validation. Notre équipe vous contactera sous 24-48h pour finaliser la publication.
          </p>
          <button 
            onClick={() => window.location.href = '/'} 
            className="bg-navy text-white px-8 py-3 rounded-xl font-bold hover:bg-navy/90 transition shadow-lg w-full"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-navy mb-3">Publier un bien</h1>
          <p className="text-gray-500 text-lg">Remplissez le formulaire ci-dessous pour mettre votre bien en ligne.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Section 1: Type */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-navy mb-6 pb-2 border-b border-gray-100">1. Type de bien</h2>
            
            <div className="mb-6">
                 <label className="block text-sm font-bold text-navy mb-3">Catégorie principale</label>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {['Location', 'Vente', 'Meublé'].map((type) => (
                        <div 
                            key={type}
                            onClick={() => handleTransactionTypeChange(type as TransactionType)}
                            className={`cursor-pointer text-center py-4 px-2 rounded-xl border-2 transition-all font-bold ${formData.transactionType === type ? 'border-orange bg-orange/5 text-orange' : 'border-gray-200 hover:border-gray-300 text-gray-500'}`}
                        >
                            {type === 'Vente' ? 'Vente-Achat' : type}
                        </div>
                    ))}
                 </div>
            </div>

            <div>
                <label className="block text-sm font-bold text-navy mb-3">Type de bien</label>
                <select 
                  name="propertyType" 
                  value={formData.propertyType} 
                  onChange={handleChange}
                  className="w-full p-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange/50 focus:border-orange outline-none font-medium text-navy"
                >
                  {getSubtypes().map(sub => <option key={sub} value={sub}>{sub}</option>)}
                </select>
            </div>
          </div>

          {/* Section 2: Localisation */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-navy mb-6 pb-2 border-b border-gray-100">2. Localisation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-navy mb-2">Zone</label>
                <select 
                  name="quartier" 
                  value={formData.quartier} 
                  onChange={handleChange}
                  className="w-full p-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange/50 outline-none text-navy"
                >
                  {ZONES.sort().map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-navy mb-2">Adresse précise / Indications</label>
                <input 
                  type="text" 
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Ex: Rue 450, à côté de la pharmacie..."
                  className="w-full p-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange/50 outline-none text-navy placeholder:text-gray-400"
                  required
                />
              </div>
            </div>
          </div>

          {/* Section 3: Caractéristiques */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-navy mb-6 pb-2 border-b border-gray-100">3. Caractéristiques & Prix</h2>
            
            <div className="mb-6">
              <label className="block text-sm font-bold text-navy mb-2">Prix (FCFA)</label>
              <div className="relative">
                <input 
                  type="number" 
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className="w-full p-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange/50 outline-none pl-4 text-navy font-bold text-lg"
                  placeholder="Ex: 150000"
                  required
                />
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-500 font-medium">
                  {formData.transactionType === 'Location' || formData.transactionType === 'Meublé' ? 'FCFA / mois' : 'FCFA'}
                </div>
              </div>
            </div>

            {showRoomFields && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Chambres</label>
                  <select name="bedrooms" value={formData.bedrooms} onChange={handleChange} className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl outline-none">
                    {[0,1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Salons</label>
                  <select name="livingRooms" value={formData.livingRooms} onChange={handleChange} className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl outline-none">
                    {[0,1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Douches</label>
                  <select name="bathrooms" value={formData.bathrooms} onChange={handleChange} className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl outline-none">
                    {[0,1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Meublé</label>
                  <select name="furnished" value={formData.furnished} onChange={handleChange} className="w-full p-3 bg-gray-50 border border-gray-300 rounded-xl outline-none">
                    <option value="Non">Non</option>
                    <option value="Oui">Oui</option>
                  </select>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-bold text-navy mb-2">Surface (m²)</label>
              <input 
                type="number" 
                name="surface"
                value={formData.surface}
                onChange={handleChange}
                className="w-full p-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange/50 outline-none text-navy"
                required
              />
            </div>
            
            {/* Features Checkboxes (Visual Simulation) */}
            <div className="mt-6">
                <label className="block text-sm font-bold text-navy mb-3">Équipements</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {['Climatisation', 'Cuisine équipée', 'Balcon', 'Garage', 'Gardiennage', 'Eau courante'].map(feat => (
                        <label key={feat} className="flex items-center space-x-2 text-sm text-gray-700">
                            <input type="checkbox" className="rounded text-orange focus:ring-orange" />
                            <span>{feat}</span>
                        </label>
                    ))}
                </div>
            </div>
          </div>

          {/* Section 4: Photos & Description */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-navy mb-6 pb-2 border-b border-gray-100">4. Photos & Détails</h2>
            
            <div className="mb-6">
              <label className="block text-sm font-bold text-navy mb-2 flex justify-between">
                  <span>Photos</span>
                  <span className={`text-xs ${photosCount >= 4 ? 'text-green-600' : 'text-orange'}`}>
                      {photosCount} / 10 (Min. 4 requises)
                  </span>
              </label>
              <div 
                className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center hover:bg-gray-50 transition cursor-pointer group"
                onClick={() => setPhotosCount(prev => Math.min(prev + 1, 10))}
              >
                <div className="w-16 h-16 bg-blue-50 text-navy rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition">
                    <UploadCloud size={32} />
                </div>
                <p className="text-gray-700 font-medium">Cliquez pour ajouter des photos</p>
                <p className="text-xs text-gray-400 mt-2">Format JPG/PNG - Max 5 Mo</p>
              </div>
              
              {/* Photo Previews (Mock) */}
              {photosCount > 0 && (
                  <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                      {Array.from({length: photosCount}).map((_, i) => (
                          <div key={i} className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0 relative overflow-hidden">
                              <img src={`https://picsum.photos/seed/${i}/200`} className="w-full h-full object-cover" alt="preview" />
                          </div>
                      ))}
                  </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-navy mb-2">Description complète</label>
              <textarea 
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={6}
                minLength={50}
                placeholder="Décrivez votre bien en détail : environnement, proximités, état général, avantages..."
                className="w-full p-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange/50 outline-none text-navy placeholder:text-gray-400"
                required
              ></textarea>
              <div className="text-right text-xs text-gray-400 mt-2">Min. 50 caractères</div>
            </div>
          </div>

          {/* Section 5: Contact */}
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-navy mb-6 pb-2 border-b border-gray-100">5. Vos coordonnées</h2>
            
            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl mb-6 flex items-start">
                <AlertCircle className="text-blue-600 mr-3 flex-shrink-0" size={20} />
                <p className="text-sm text-blue-800">
                    Ces informations resteront confidentielles. Seul le contact LDF sera affiché publiquement.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-navy mb-2">Vous êtes</label>
                <div className="flex space-x-4">
                  {['Propriétaire', 'Agent immobilier'].map((type) => (
                    <label key={type} className="flex items-center space-x-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="advertiserType" 
                        value={type}
                        checked={formData.advertiserType === type}
                        onChange={handleChange}
                        className="text-orange focus:ring-orange" 
                      />
                      <span className="text-gray-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-navy mb-2">Nom complet</label>
                <input 
                    type="text" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleChange} 
                    className="w-full p-4 bg-gray-50 border border-gray-300 rounded-xl outline-none" 
                    required 
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-navy mb-2">Téléphone WhatsApp</label>
                <input 
                    type="tel" 
                    name="phone" 
                    value={formData.phone} 
                    onChange={handleChange} 
                    className="w-full p-4 bg-gray-50 border border-gray-300 rounded-xl outline-none" 
                    placeholder="+229..." 
                    required 
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4 pb-12">
            <button 
              type="submit" 
              className="w-full bg-orange text-white text-lg font-bold py-4 rounded-xl shadow-lg hover:bg-orange/90 transition transform hover:-translate-y-1"
            >
              Publier mon annonce
            </button>
            <p className="text-center text-gray-500 text-sm mt-4">
              En publiant, vous acceptez nos conditions générales d'utilisation.
            </p>
          </div>

        </form>
      </div>
    </div>
  );
};

export default Publish;
